import { RefObject, useEffect } from "react";

export type UseScrollOnExpandOptions = {
  duration?: number;
  scrollContainerSelector?: string;
};

export const useScrollOnExpand = (
  isExpanded: boolean,
  wrapperRef: RefObject<HTMLElement | null>,
  options?: UseScrollOnExpandOptions,
) => {
  const { duration = 300, scrollContainerSelector = ".overflow-y-auto" } =
    options || {};

  useEffect(() => {
    if (!isExpanded) return;

    const scrollContainer = wrapperRef.current?.closest(
      scrollContainerSelector,
    ) as HTMLElement;
    if (!scrollContainer) return;

    const startTime = performance.now();
    const startScroll = scrollContainer.scrollTop;
    let animationFrameId: number;

    const animateScroll = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      const easeOut = 1 - Math.pow(1 - progress, 3);

      const currentScrollHeight = scrollContainer.scrollHeight;
      const targetScroll = currentScrollHeight - scrollContainer.clientHeight;

      const newScroll = startScroll + (targetScroll - startScroll) * easeOut;
      scrollContainer.scrollTop = newScroll;

      if (progress < 1) {
        animationFrameId = requestAnimationFrame(animateScroll);
      }
    };

    animationFrameId = requestAnimationFrame(animateScroll);

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [isExpanded, duration, scrollContainerSelector, wrapperRef]);
};
